import { AlertCircle, CheckCircle2, ClipboardList, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { useAuth } from "@/app/context/Auth/AuthContext";
import { cn } from "@/app/handlers/cn.utils";
import {
	getEndOfLastMonth,
	getStartOfLastMonth,
	getStartOfMonth,
	getStartOfWeek,
} from "@/app/handlers/utilityFunctions";

import Loader from "../../components/Loader";
import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import Header from "../Profile/components/Header";
import FitCheckCard from "./components/FitCheckCard";
import FitCheckDetailDrawer from "./components/FitCheckDetailDrawer";
import StatsCard from "./components/StatsCard";
import { FitCheckResponse, useFitChecks } from "./config/api";

type FilterTab = "all" | "pending" | "ready" | "prepare";

function FitCheckDashboard() {
	const navigate = useNavigate();
	const isLoggedIn = useIsLoggedIn();
	const { userData } = useAuth();
	const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
	const [selectedFitCheck, setSelectedFitCheck] = useState<FitCheckResponse | null>(null);
	const [isScrolled, setIsScrolled] = useState(false);

	const { data: fitChecks, isLoading, isError } = useFitChecks(userData?.userID || "");

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 0);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const stats = useMemo(() => {
		if (!fitChecks) return { total: 0, gapsClosed: 0, credits: 5, usedThisMonth: 0, percentChange: 0 };

		const now = new Date();
		const startOfWeek = getStartOfWeek(now);
		const startOfMonth = getStartOfMonth(now);
		const startOfLastMonth = getStartOfLastMonth(now);
		const endOfLastMonth = getEndOfLastMonth(now);

		let gapsClosedCount = 0;
		let usedThisWeek = 0;
		let usedThisMonth = 0;
		let usedLastMonth = 0;

		fitChecks.forEach((fc) => {
			const baselineCheck = fc.fitCheck.baselineCheck;
			if (baselineCheck.education.status === "MEET") gapsClosedCount++;
			if (baselineCheck.experience.status === "MEET") gapsClosedCount++;
			if (baselineCheck.skills.status === "MEET") gapsClosedCount++;
			if (baselineCheck.certification.status === "MEET") gapsClosedCount++;

			// Date-based calculations
			const createdAt = new Date(fc.createdAt);

			if (createdAt >= startOfWeek) {
				usedThisWeek++;
			}

			if (createdAt >= startOfMonth) {
				usedThisMonth++;
			}

			if (createdAt >= startOfLastMonth && createdAt <= endOfLastMonth) {
				usedLastMonth++;
			}
		});

		// Calculate percentage change from last month
		let percentChange = 0;
		if (usedLastMonth > 0) {
			percentChange = Math.round(((usedThisMonth - usedLastMonth) / usedLastMonth) * 100);
		} else if (usedThisMonth > 0) {
			percentChange = 100; // If no FitChecks last month but some this month, show +100%
		}

		// Credits: 5 per week, subtract what's been used this week
		const credits = Math.max(0, 5 - usedThisWeek);

		return {
			total: fitChecks.length,
			gapsClosed: gapsClosedCount,
			credits,
			usedThisMonth,
			percentChange,
		};
	}, [fitChecks]);

	const filterCounts = useMemo(() => {
		if (!fitChecks) return { all: 0, pending: 0, ready: 0, prepare: 0 };

		let readyCount = 0;
		let prepareCount = 0;

		fitChecks.forEach((fc) => {
			const bc = fc.fitCheck.baselineCheck;
			const meetsCount = [bc.education, bc.experience, bc.skills, bc.certification].filter(
				(i) => i.status === "MEET",
			).length;
			if (meetsCount >= 3) {
				readyCount++;
			} else {
				prepareCount++;
			}
		});

		return {
			all: fitChecks.length,
			pending: 3, // Placeholder for pending items
			ready: readyCount,
			prepare: prepareCount,
		};
	}, [fitChecks]);

	const filteredFitChecks = useMemo(() => {
		if (!fitChecks) return [];

		return fitChecks.filter((fc) => {
			if (activeFilter === "all") return true;
			if (activeFilter === "pending") return false; // No pending for now

			const baselineCheck = fc.fitCheck.baselineCheck;
			const meetsCount = [
				baselineCheck.education,
				baselineCheck.experience,
				baselineCheck.skills,
				baselineCheck.certification,
			].filter((i) => i.status === "MEET").length;

			if (activeFilter === "ready") return meetsCount >= 3;
			if (activeFilter === "prepare") return meetsCount < 3;
			return true;
		});
	}, [fitChecks, activeFilter]);

	const filterTabs: {
		key: FilterTab;
		label: string;
		count: number;
		variant: "default" | "pending" | "ready" | "prepare";
	}[] = [
		{ key: "all", label: "All FitChecks", count: filterCounts.all, variant: "default" },
		{ key: "pending", label: "Pending", count: filterCounts.pending, variant: "pending" },
		{ key: "ready", label: "Ready to Apply", count: filterCounts.ready, variant: "ready" },
		{ key: "prepare", label: "Prepare First", count: filterCounts.prepare, variant: "prepare" },
	];

	return (
		<>
			{/* Detail Drawer */}
			<FitCheckDetailDrawer selectedFitCheck={selectedFitCheck} onClose={() => setSelectedFitCheck(null)} />
			{/* Header */}
			<div className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-xs" : ""}`}>
				<Header isLoggedIn={isLoggedIn} variant="applicant" />
			</div>

			<div className="min-h-screen bg-gray-50">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					{/* Stats Cards */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
						<StatsCard
							title="Total FitChecks"
							value={stats.total}
							subtitle={
								stats.percentChange >= 0
									? `+${stats.percentChange}% from last month`
									: `${stats.percentChange}% from last month`
							}
							subtitleColor={stats.percentChange >= 0 ? "text-green-600" : "text-red-500"}
							icon={<ClipboardList className="w-6 h-6" />}
						/>
						<StatsCard
							title="Gaps Closed"
							value={stats.gapsClosed}
							subtitle={`+${stats.usedThisMonth} this month`}
							subtitleColor="text-green-600"
							icon={<CheckCircle2 className="w-6 h-6" />}
						/>
						<StatsCard
							title="Credits Available"
							value={stats.credits}
							subtitle={`${stats.usedThisMonth} used this month`}
							subtitleColor="text-red-500"
							icon={
								<div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center">
									<span className="text-yellow-600 text-xs font-bold">$</span>
								</div>
							}
						/>
					</div>

					{/* Filters and New FitCheck Button */}
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
						<div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
							{filterTabs.map((tab) => {
								const isActive = activeFilter === tab.key;
								let bgClass = "";
								let textClass = "";
								let countBgClass = "";

								if (isActive) {
									if (tab.variant === "pending") {
										bgClass = "bg-red-500";
										textClass = "text-white";
										countBgClass = "bg-white/20";
									} else if (tab.variant === "ready") {
										bgClass = "bg-green-600";
										textClass = "text-white";
										countBgClass = "bg-white/20";
									} else if (tab.variant === "prepare") {
										bgClass = "bg-blue-600";
										textClass = "text-white";
										countBgClass = "bg-white/20";
									} else {
										bgClass = "bg-primary-600";
										textClass = "text-white";
										countBgClass = "bg-white/20";
									}
								} else {
									bgClass = "bg-white border border-gray-200";
									textClass = "text-gray-600";
									countBgClass = "bg-gray-100";
								}

								return (
									<button
										key={tab.key}
										onClick={() => setActiveFilter(tab.key)}
										className={cn(
											"px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2",
											bgClass,
											textClass,
										)}
									>
										{tab.label}
										<span
											className={cn(
												"px-2 py-0.5 rounded-full text-xs",
												countBgClass,
												isActive ? "text-white" : "text-gray-500",
											)}
										>
											{tab.count}
										</span>
									</button>
								);
							})}
						</div>

						<Button
							onClick={() => navigate("/jobs")}
							className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2"
						>
							<Plus className="w-4 h-4" />
							New FitCheck
						</Button>
					</div>

					{/* Content */}
					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<Loader />
						</div>
					) : isError ? (
						<div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
							<AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
							<p className="text-red-700">Failed to load FitCheck history. Please try again.</p>
							<Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
								Retry
							</Button>
						</div>
					) : filteredFitChecks.length === 0 ? (
						<div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
							<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<CheckCircle2 className="w-8 h-8 text-gray-400" />
							</div>
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								{activeFilter === "all"
									? "No FitChecks yet"
									: `No ${filterTabs.find((t) => t.key === activeFilter)?.label} FitChecks`}
							</h3>
							<p className="text-gray-500 mb-6 max-w-sm mx-auto">
								{activeFilter === "all"
									? "Run your first FitCheck from a job listing to see how well you match."
									: "Try changing the filter to see other FitChecks."}
							</p>
							{activeFilter === "all" && (
								<Button onClick={() => navigate("/jobs")} className="bg-primary-600 hover:bg-primary-700">
									Browse Jobs
								</Button>
							)}
						</div>
					) : (
						<div className="space-y-4">
							{filteredFitChecks.map((fitCheck) => (
								<FitCheckCard
									key={`${fitCheck.jobId}-${fitCheck.createdAt}`}
									fitCheck={fitCheck}
									onViewDetails={() => setSelectedFitCheck(fitCheck)}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export default FitCheckDashboard;
